/**
 * Messages API client — In-App Messaging (Epic 5)
 */

import { authenticatedFetch } from './authApi'

const GATEWAY_BASE = (import.meta.env.DEV && import.meta.env.VITE_USE_API_PROXY)
  ? ''
  : (import.meta.env.VITE_API_GATEWAY_URL || 'http://moneybridge-feature-api.australiaeast.azurecontainer.io:8000')

function authHeaders(token: string) {
  return { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` }
}

async function handleResponse<T>(res: Response): Promise<T> {
  if (!res.ok) {
    const err = await res.json().catch(() => ({}))
    throw new Error(err.detail || `HTTP ${res.status}`)
  }
  if (res.status === 204) return undefined as T
  return res.json()
}

// ── Types ─────────────────────────────────────────────────────────────────────

export interface Conversation {
  id: string
  customer_id: string
  broker_id: string
  last_message_at: string | null
  created_at: string | null
  other_party_name: string | null
  last_message_preview: string | null
  unread_count: number
}

export interface Message {
  id: string
  conversation_id: string
  sender_id: string
  content: string
  message_type: 'text' | 'system'
  is_read: boolean
  created_at: string | null
}

export interface ConversationListResponse {
  conversations: Conversation[]
  count: number
}

export interface MessagesResponse {
  messages: Message[]
  count: number
  has_more: boolean
}

// ── Conversations ─────────────────────────────────────────────────────────────

export async function createOrGetConversation(
  token: string,
  body: { other_user_id: string },
): Promise<Conversation> {
  const res = await authenticatedFetch(`${GATEWAY_BASE}/api/messages/conversations`, {
    method: 'POST',
    headers: authHeaders(token),
    body: JSON.stringify(body),
  })
  return handleResponse<Conversation>(res)
}

export async function listConversations(token: string): Promise<ConversationListResponse> {
  const res = await authenticatedFetch(`${GATEWAY_BASE}/api/messages/conversations`, {
    headers: authHeaders(token),
  })
  return handleResponse<ConversationListResponse>(res)
}

export async function getUnreadCount(token: string): Promise<{ unread_count: number }> {
  const res = await authenticatedFetch(`${GATEWAY_BASE}/api/messages/unread-count`, {
    headers: authHeaders(token),
  })
  return handleResponse<{ unread_count: number }>(res)
}

// ── Messages ──────────────────────────────────────────────────────────────────

export async function getMessages(
  token: string,
  conversationId: string,
  page?: number,
): Promise<MessagesResponse> {
  const params = page && page > 1 ? `?page=${page}` : ''
  const res = await authenticatedFetch(
    `${GATEWAY_BASE}/api/messages/conversations/${conversationId}${params}`,
    { headers: authHeaders(token) },
  )
  return handleResponse<MessagesResponse>(res)
}

export async function sendMessage(
  token: string,
  conversationId: string,
  content: string,
): Promise<Message> {
  const res = await authenticatedFetch(
    `${GATEWAY_BASE}/api/messages/conversations/${conversationId}/messages`,
    {
      method: 'POST',
      headers: authHeaders(token),
      body: JSON.stringify({ content }),
    },
  )
  return handleResponse<Message>(res)
}
